import { isLoopbackUrl } from '@/lib/utils/cliAuth';

export type LoginParams = {
  callbackURL: string | null;
  extensionName: string | null;
  cli: boolean;
  cliRedirect: string | null;
  cliState: string | null;
};

export type PostLoginDecision =
  | { type: 'redirect'; to: string }
  | { type: 'cli-finish'; to: string }
  | {
      type: 'cli-handoff';
      cliRedirect: string | null;
      cliState: string | null;
    };

const INTERNAL_BASE = 'http://internal.invalid';

function first(value: string | string[] | undefined): string | null {
  const v = Array.isArray(value) ? value[0] : value;
  return v ? v : null;
}

export function parseLoginSearchParams(
  sp: Record<string, string | string[] | undefined>
): LoginParams {
  return {
    // `callbackUrl` is the name the MCP OAuth page sends.
    callbackURL: first(sp.callbackURLAfterSignIn) ?? first(sp.callbackUrl),
    extensionName: first(sp.extensionName),
    cli: first(sp.cli) === 'true',
    cliRedirect: first(sp.cli_redirect),
    cliState: first(sp.cli_state),
  };
}

function isAuthPage(pathname: string): boolean {
  return ['/login', '/signup'].some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

// Open-redirect guard: only same-origin paths that are not the auth pages.
export function safeInternalPath(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return null;
  }
  if (raw.startsWith('/\\')) {
    return null;
  }
  try {
    const url = new URL(raw, INTERNAL_BASE);
    if (url.origin !== INTERNAL_BASE || isAuthPage(url.pathname)) {
      return null;
    }
    const normalized = url.pathname + url.search + url.hash;
    // Dot segments can collapse to `//host` or `/\host`, which redirect() sends off-site.
    if (normalized.startsWith('//') || normalized.startsWith('/\\')) {
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}

function cliFinish(params: LoginParams): PostLoginDecision {
  if (isLoopbackUrl(params.cliRedirect)) {
    const q = new URLSearchParams({
      cli_redirect: params.cliRedirect ?? '',
      cli_state: params.cliState ?? '',
    });
    return { type: 'cli-finish', to: `/cli-auth?${q.toString()}` };
  }
  return { type: 'cli-finish', to: '/settings?tab=api' };
}

export function resolvePostLoginDestination(input: {
  params: LoginParams;
  user: { UserUUID?: string | null; OrganizationUUID?: string | null } | null;
  org: {
    RequiresOnboarding?: boolean | null;
    OnboardingCompleted?: boolean | null;
  } | null;
}): PostLoginDecision {
  const { params, user, org } = input;
  const callback = safeInternalPath(params.callbackURL);

  // Loose prefix on purpose: invite links send `/join-workspace?...` with no slash.
  if (callback?.startsWith('/join-workspace')) {
    return { type: 'redirect', to: callback };
  }
  // Ahead of the user check: /extension/login resolves the user itself.
  if (params.extensionName) {
    return {
      type: 'redirect',
      to: `/extension/login?extensionName=${encodeURIComponent(params.extensionName)}`,
    };
  }
  // A failed identity lookup (getServerUser swallows backend errors to null)
  // must not drop the destination. /cli-auth resolves the user itself; the
  // dashboard client resolves /onboarding and /account-mismatch.
  if (!user?.UserUUID) {
    return params.cli
      ? cliFinish(params)
      : { type: 'redirect', to: callback ?? '/home' };
  }
  // No org yet: onboarding creates one. A callback must not skip it.
  if (!user.OrganizationUUID) {
    return params.cli
      ? {
          type: 'cli-handoff',
          cliRedirect: params.cliRedirect,
          cliState: params.cliState,
        }
      : { type: 'redirect', to: '/onboarding' };
  }
  // CLI skips the org onboarding flags.
  if (params.cli) {
    return cliFinish(params);
  }
  if (org?.OnboardingCompleted !== true && org?.RequiresOnboarding === true) {
    return { type: 'redirect', to: '/onboarding' };
  }
  return { type: 'redirect', to: callback ?? '/home' };
}

export function buildSignInCallbackPath(params: LoginParams): string {
  const q = new URLSearchParams();
  if (params.callbackURL) q.set('callbackURLAfterSignIn', params.callbackURL);
  if (params.extensionName) q.set('extensionName', params.extensionName);
  if (params.cli) q.set('cli', 'true');
  if (params.cliRedirect) q.set('cli_redirect', params.cliRedirect);
  if (params.cliState) q.set('cli_state', params.cliState);
  const qs = q.toString();
  return qs ? `/login?${qs}` : '/login';
}
